<template lang="html">
  <div>
    <div class="fsd-login">
      <div class="fsd-session__headline">
        {{ headline }}
      </div>
      <div
        v-if="login.failure && login.failure.type === 'passwordLogin'"
        class="form-error"
      >
        <div v-if="[400, 401, 403].includes(login.failure.status)">
          <p>{{ 'client.login_modal.error.message' | t }}</p>
          <p>{{ 'client.login_modal.error.notice' | t }}</p>
        </div>
        <div v-else>
          <p>{{ 'client.login_modal.error.default' | t }}</p>
        </div>
      </div>
      <div
        v-if="
          login.failure &&
          ['passwordRequest', 'emailRequest', 'emailLogin'].includes(
            login.failure.type
          )
        "
        class="form-error"
      >
        <p>
          {{ `client.registration_modal.error.${login.failure.status}` | t }}
        </p>
      </div>
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-if="reason === 'email_change'"
        class="fsd-login__reason"
        v-html="
          $options.filters.t('client.registration.reauthentication.notice_html')
        "
      />
      <!-- eslint-enable vue/no-v-html -->
      <div class="fsd-login__form row align-center align-middle large-collapse">
        <div
          v-show="currentPage === 'emailLogin'"
          class="fsd-login__page fsd-login__page--email row align-center"
        >
          <template v-if="login.emailLoginEmail">
            <EmailSent type="emailLogin" />
          </template>
          <form
            v-else
            ref="emailLoginForm"
            v-tracking-context="{ event_element: 'email' }"
            class="columns small-10 medium-8 large-7"
            action=""
            @submit.prevent="submitEmailLogin"
          >
            <input
              v-model="emailLoginInput"
              type="text"
              name="email"
              :placeholder="passwordLoginPlaceholders.email | t"
              class="input input--login input--validate"
              :class="{ 'input--invalid': login.failure === 'emailLogin' }"
            />
            <button
              ref="submitEmailLogin"
              v-click-track="{ action_type: 'login' }"
              class="button button--secondary expanded"
            >
              {{ 'client.login.email_login.buttons.email' | t }}
            </button>
            <button
              v-click-track="{ action_type: 'login' }"
              class="button button--secondary expanded"
              @click.prevent="loginCurrentPageSet('passwordLogin')"
            >
              {{ 'client.login.email_login.buttons.password' | t }}
            </button>
            <div
              v-if="jobMarket !== 'ZEIT_JOBS'"
              v-tracking-context="{ event_element: 'social' }"
              class="social-login"
            >
              <socialLogin
                :search-profile="currentSearchProfile"
                :rc="registrationCampaignId"
              />
            </div>
          </form>
        </div>

        <div
          v-show="currentPage === 'passwordLogin'"
          class="fsd-login__page fsd-login__page--password row align-center"
        >
          <form
            ref="passwordLoginForm"
            v-tracking-context="{ event_element: 'email' }"
            class="columns small-10 medium-8 large-7"
            action=""
            @submit.prevent="submitPasswordLogin"
          >
            <input
              v-model="passwordLoginEmailInput"
              type="text"
              name="email"
              :placeholder="passwordLoginPlaceholders.email | t"
              class="input input--login input--validate"
              :class="{ 'input--invalid': login.failure === 'passwordLogin' }"
            />
            <div class="input-password">
              <span v-if="!passwordVisible" @click="togglePasswordVisible">
                <svg-icon
                  icon-name="make-visible"
                  class-name="input-password__icon"
                />
              </span>
              <span v-else @click="togglePasswordVisible">
                <svg-icon
                  icon-name="make-invisible"
                  class-name="input-password__icon"
                  @click="togglePasswordVisible"
                />
              </span>

              <input
                v-model="passwordLoginPasswordInput"
                name="password"
                :type="passwordType"
                :placeholder="passwordLoginPlaceholders.password | t"
                class="input input--login input--validate"
                :class="{ 'input--invalid': login.failure === 'passwordLogin' }"
              />
            </div>

            <button
              ref="submitPasswordLogin"
              v-click-track="{ action_type: 'login' }"
              class="button button--secondary expanded"
            >
              {{ 'client.login.password_login.button' | t }}
            </button>
          </form>
        </div>

        <div
          v-show="currentPage === 'troubleshooting'"
          class="fsd-login__page fsd-login__page--troubleshooting row align-center"
        >
          <div class="fsd-login__troubleshooting">
            <a
              v-for="(option, idx) in troubleshootingOptions"
              :key="`option${idx}`"
              class="fsd-login__troubleshooting-box"
              @click="loginCurrentPageSet(option.page)"
            >
              <div class="fsd-login__troubleshooting-icon-container">
                <svg-icon
                  :icon-name="option.icon"
                  class="fsd-login__troubleshooting-icon"
                />
              </div>
              <!-- eslint-disable vue/no-v-html -->
              <div
                v-click-track="{
                  event_element: option.name,
                  action_type: 'visit'
                }"
                class="fsd-login__troubleshooting-hint"
                v-html="$options.filters.t(option.translationKey)"
              />
              <!-- eslint-enable vue/no-v-html -->
              <div class="fsd-login__troubleshooting-chevron">
                <svg-icon
                  class="fsd-login__troubleshooting-chevron-icon"
                  icon-name="chevron-right"
                />
              </div>
            </a>
          </div>
        </div>

        <div
          v-show="currentPage === 'passwordRequest'"
          class="fsd-login__page fsd-login__page--new-password row align-center"
        >
          <template v-if="login.passwordRequestEmail">
            <EmailSent type="passwordRequest" />
          </template>
          <div v-else class="row align-center">
            <div class="fsd-login__page-hint columns small-10 medium-9 large-9">
              {{ 'client.login.password_request.text' | t }}
            </div>

            <form
              ref="passwordRequestForm"
              v-tracking-context="{ event_element: 'email' }"
              class="columns small-10 medium-8 large-7"
              action=""
              @submit.prevent="submitPasswordReset"
            >
              <input
                v-model="passwordRequestInput"
                type="text"
                name="passwordRequestEmail"
                :placeholder="'client.login_modal.email' | t"
                class="input input--login input--validate"
                :class="{
                  'input--invalid': login.failure === 'passwordRequest'
                }"
              />
              <button
                ref="submitPasswordReset"
                v-click-track="{ action_type: 'request_password' }"
                class="button button--secondary expanded"
              >
                {{ 'client.login.password_request.button' | t }}
              </button>
            </form>
          </div>
        </div>

        <div
          v-show="currentPage === 'emailRequest'"
          class="fsd-login__page fsd-login__page--retry row align-center"
        >
          <template v-if="login.emailRequestEmail">
            <EmailSent type="emailRequest" />
          </template>
          <div v-else class="row align-center">
            <div class="fsd-login__page-hint columns small-10 medium-9 large-9">
              {{ 'client.login.email_request.text' | t }}
            </div>
            <form
              ref="emailRequestForm"
              v-tracking-context="{ event_element: 'email' }"
              class="columns small-10 medium-8 large-7"
              action=""
              @submit.prevent="submitEmailRequest"
            >
              <input
                v-model="emailRequestInput"
                type="text"
                name="emailRequestEmail"
                :placeholder="'client.login_modal.email' | t"
                class="input input--login input--validate"
                :class="{ 'input--invalid': login.failure === 'emailRequest' }"
              />
              <button
                ref="submitEmailRequest"
                v-click-track="{ action_type: 'request_password' }"
                class="button button--secondary expanded"
              >
                {{ 'client.login.email_request.button' | t }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _get from 'lodash-es/get'
import { mapState, mapMutations, mapActions } from 'vuex'
import { manualTrack } from '@/vue/directives/tracking'
import SocialLogin from '@/vue/components/fused/sessions/social_login'
import EmailSent from '@/vue/components/fused/sessions/email_sent'

const HEADLINES = {
  emailLogin: {
    prompt: 'client.login.email_login.headline',
    success: 'client.email_sent.headline'
  },
  passwordLogin: {
    prompt: 'client.login.password_login.headline'
  },
  emailRequest: {
    prompt: 'client.login.email_request.headline',
    success: 'client.email_sent.headline'
  },
  passwordRequest: {
    prompt: 'client.login.password_request.headline',
    success: 'client.email_sent.headline'
  },
  troubleshooting: {
    prompt: 'client.login.troubleshooting.headline'
  }
}

const TROUBLESHOOTING_OPTIONS = [
  {
    name: 'noEmailReceived',
    page: 'emailRequest',
    translationKey: 'client.login.troubleshooting.no_email_received.label_html',
    icon: 'modern-mail'
  },
  {
    name: 'noPassword',
    page: 'passwordRequest',
    translationKey: 'client.login.troubleshooting.no_password.label_html',
    icon: 'keyhole'
  },
  {
    name: 'forgottenPassword',
    page: 'passwordRequest',
    translationKey:
      'client.login.troubleshooting.forgotten_password.label_html',
    icon: 'password'
  }
]

export default {
  components: { EmailSent, SocialLogin },
  props: {
    returnTo: { type: String, default: null },
    reason: { type: String, default: null },
    shouldRedirectAfterLogin: { type: Boolean, default: false },
    registrationCampaignId: { type: String, default: null },
    currentSearchProfile: {
      type: Object,
      default: () => ({
        tagLabels: null,
        name: null,
        query: null,
        code: null,
        isActive: null
      })
    }
  },
  data: () => ({
    passwordRequestInput: '',
    passwordLoginEmailInput: '',
    passwordLoginPasswordInput: '',
    emailRequestInput: '',
    emailLoginInput: '',
    passwordVisible: false,
    troubleshootingOptions: TROUBLESHOOTING_OPTIONS
  }),
  computed: {
    ...mapState([
      'links',
      'csrfToken',
      'searchProfile',
      'user',
      'modalProps',
      'links',
      'jobMarket',
      'login',
      'initialIntent',
      'initialIntentData'
    ]),
    currentPage() {
      return this.login.currentPage
    },
    passwordType() {
      return this.passwordVisible ? 'text' : 'password'
    },
    headline() {
      // check if store has a mail for login process of current page
      const status = this.login[`${this.currentPage}Email`]
        ? 'success'
        : 'prompt'
      // and choose translation key accordingly
      const translationKey = HEADLINES[this.currentPage][status]

      return this.$options.filters.t(translationKey)
    },
    passwordLoginPlaceholders() {
      if (this.reason === 'email_change') {
        return {
          email: 'client.registration.reauthentication.email',
          password: 'client.registration.reauthentication.password'
        }
      } else {
        return {
          email: 'client.login_modal.email',
          password: 'client.login_modal.password'
        }
      }
    }
  },
  mounted() {
    ;[
      'passwordRequestInput',
      'passwordLoginEmailInput',
      'emailRequestInput',
      'emailLoginInput'
    ].forEach(input => {
      this[input] = _get(this.user, 'email') || ''
    })
    if (this.reason === 'email_change') {
      this.loginCurrentPageSet('passwordLogin')
    }
  },
  methods: {
    ...mapActions([
      'redirect',
      'userLogin',
      'userEmailLogin',
      'saveSearchProfile',
      'requestPasswordReset',
      'loginFailureReset',
      'loginFailureSet',
      'loginCurrentPageSet',
      'loginEmailSet',
      'fullfillInitialIntent'
    ]),
    ...mapMutations(['userSet', 'modalClose']),
    submitPasswordReset() {
      this.loginFailureReset()

      this.requestPasswordReset({ email: this.passwordRequestInput })
        .then(result => {
          this.loginEmailSet({ type: 'passwordRequest', email: result.email })
        })
        .catch(error => {
          const httpStatus =
            error.response?.status === 400 ? error.response.status : 'default'
          this.loginFailureSet({ type: 'passwordRequest', status: httpStatus })
        })
    },
    submitEmailLogin() {
      this.loginFailureReset()

      this.userEmailLogin({
        email: this.emailLoginInput,
        returnTo: this.returnTo
      })
        .then(result => {
          this.loginEmailSet({ type: 'emailLogin', email: result.email })
          this.loginCurrentPageSet('emailLogin')
        })
        .catch(error => {
          const httpStatus =
            error.response?.status === 400 ? error.response.status : 'default'
          this.loginFailureSet({ type: 'emailLogin', status: httpStatus })
        })
    },
    submitPasswordLogin() {
      this.loginFailureReset()

      this.userLogin({
        email: this.passwordLoginEmailInput,
        password: this.passwordLoginPasswordInput,
        returnTo: this.returnTo
      })
        .then(result => {
          if (this.shouldRedirectAfterLogin) {
            this.redirect(result.returnTo)
          } else {
            // TODO: pull this mutation out of the component?
            this.userSet(result.state.user)
            this.fullfillInitialIntent()
            EventBus.trigger('user:loggedIn')
            this.modalClose()
          }
        })
        .catch(error => {
          const httpStatus = [400, 401, 403].includes(error.response?.status)
            ? error.response.status
            : 'default'
          this.loginFailureSet({ type: 'passwordLogin', status: httpStatus })
          manualTrack(this.$refs.submitPasswordLogin, {
            action_type: 'login_failed'
          })
        })
    },
    submitEmailRequest() {
      this.loginFailureReset()

      this.userEmailLogin({
        email: this.emailRequestInput,
        returnTo: this.returnTo
      })
        .then(result => {
          this.loginEmailSet({ type: 'emailRequest', email: result.email })
        })
        .catch(error => {
          const httpStatus =
            error.response?.status === 400 ? error.response.status : 'default'
          this.loginFailureSet({ type: 'emailRequest', status: httpStatus })
        })
    },
    togglePasswordVisible() {
      this.passwordVisible = !this.passwordVisible
    }
  }
}
</script>
